import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";

// Pages — existentes
import UploadPage from "./pages/UploadPage";
import DashboardPage from "./pages/DashboardPage";
import NotasPage from "./pages/NotasPage";
import InspecaoPage from "./pages/InspecaoPage";
import RelatorioPage from "./pages/RelatorioPage";
import AdminPage from "./pages/AdminPage";
import NotFound from "./pages/NotFound";

// Pages — novas
import ProdutosPage from "./pages/ProdutosPage";
import TributacaoPage from "./pages/TributacaoPage";
import ClassificacaoFiscalPage from "./pages/ClassificacaoFiscalPage";
import ValidacaoXmlPage from "./pages/ValidacaoXmlPage";
import ExportacoesPage from "./pages/ExportacoesPage";
import ParametrosPage from "./pages/ParametrosPage";
import RegrasPage from "./pages/RegrasPage";
import UsuariosPage from "./pages/UsuariosPage";
import PlanosPage from "./pages/PlanosPage";
import AssinaturasAdminPage from "./pages/AssinaturasAdminPage";
import MinhaAssinaturaPage from "./pages/MinhaAssinaturaPage";

// Auth & Legal
import LoginPage from "./pages/auth/LoginPage";
import SignupPage from "./pages/auth/SignupPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import AuthCallbackPage from "./pages/auth/AuthCallbackPage";
import PrivacidadePage from "./pages/legal/PrivacidadePage";
import TermosPage from "./pages/legal/TermosPage";
import SobrePage from "./pages/legal/SobrePage";
import AtualizacoesPage from "./pages/legal/AtualizacoesPage";
import CookiesPage from "./pages/legal/CookiesPage";
import DPOPage from "./pages/legal/DPOPage";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";

const queryClient = new QueryClient();


const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Auth */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />

            {/* Legal */}
            <Route path="/privacidade" element={<PrivacidadePage />} />
            <Route path="/termos" element={<TermosPage />} />
            <Route path="/sobre" element={<SobrePage />} />
            <Route path="/atualizacoes" element={<AtualizacoesPage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="/contato-dpo" element={<DPOPage />} />
            <Route path="/planos" element={<PlanosPage />} />

            {/* App protegido */}
            <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
              <Route path="/" element={<Navigate to="/upload" replace />} />

              {/* OPERAÇÃO */}
              <Route path="/upload" element={<UploadPage />} />

              {/* VISÃO GERAL */}
              <Route path="/dashboard" element={<DashboardPage />} />

              {/* ANÁLISE FISCAL */}
              <Route path="/notas" element={<NotasPage />} />
              <Route path="/produtos" element={<ProdutosPage />} />
              <Route path="/tributacao" element={<TributacaoPage />} />
              <Route path="/classificacao" element={<ClassificacaoFiscalPage />} />

              {/* INSPEÇÃO E INDÍCIOS */}
              <Route path="/inspecao" element={<InspecaoPage />} />
              <Route path="/validacao-xml" element={<ValidacaoXmlPage />} />

              {/* RELATÓRIOS */}
              <Route path="/relatorio" element={<RelatorioPage />} />
              <Route path="/exportacoes" element={<ExportacoesPage />} />

              {/* ADMINISTRAÇÃO UNIFICADA */}
              <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminPage /></ProtectedRoute>} />
              <Route path="/parametros" element={<Navigate to="/admin" replace />} />
              <Route path="/regras" element={<Navigate to="/admin" replace />} />
              <Route path="/usuarios" element={<Navigate to="/admin" replace />} />
              <Route path="/admin/assinaturas" element={<Navigate to="/admin" replace />} />
              <Route path="/minha-assinatura" element={<MinhaAssinaturaPage />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
          <CookieConsentBanner />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
